import { httpClient } from '@/infrastructure/api/httpClient';
import type { AdminRepository } from '@/domain/port/AdminRepository';
import type { AdminChapter } from '@/domain/model/AdminChapter';
import type { AdminCourse } from '@/domain/model/AdminCourse';
import type { AdminStats } from '@/domain/model/AdminStats';
import type { AdminSubcategory } from '@/domain/model/AdminSubcategory';
import type { AdminUser } from '@/domain/model/AdminUser';
import type { AdminUserProfile } from '@/domain/model/AdminUserProfile';
import type { Role } from '@/domain/model/Role';
import type { Category } from '@/domain/model/Category';
import type { CourseUploadFiles, NewCourseFiles } from '@/domain/model/CourseUploadFiles';

type CoursePayload =
  Parameters<AdminRepository['createCourse']>[0] | Parameters<AdminRepository['updateCourse']>[1];

interface UploadConfig {
  headers: { 'Content-Type': string };
  onUploadProgress: (event: { loaded: number; total?: number }) => void;
}

const buildCourseFormData = (data: CoursePayload, files: CourseUploadFiles): FormData => {
  const formData = new FormData();
  formData.append('course', new Blob([JSON.stringify(data)], { type: 'application/json' }));
  if (files.media) {
    formData.append('media', files.media);
  }
  files.documents.forEach((pdf) => formData.append('documents', pdf));
  return formData;
};

const uploadConfig = (onProgress?: (progress: number) => void): UploadConfig => ({
  headers: { 'Content-Type': 'multipart/form-data' },
  onUploadProgress: (event: { loaded: number; total?: number }) => {
    if (onProgress && event.total) {
      onProgress(Math.round((event.loaded * 100) / event.total));
    }
  },
});

export class AdminApiAdapter implements AdminRepository {
  async getStats(): Promise<AdminStats> {
    const response = await httpClient.get<AdminStats>('/admin/stats');
    return response.data;
  }

  async getUsers(search?: string): Promise<AdminUser[]> {
    const params = search ? { search } : undefined;
    const response = await httpClient.get<AdminUser[]>('/admin/users', { params });
    return response.data;
  }

  async getUserProfile(id: string): Promise<AdminUserProfile> {
    const response = await httpClient.get<AdminUserProfile>(`/admin/users/${id}`);
    return response.data;
  }

  async changeUserRole(id: string, role: Role): Promise<AdminUser> {
    const response = await httpClient.put<AdminUser>(`/admin/users/${id}/role`, { role });
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await httpClient.delete(`/admin/users/${id}`);
  }

  async getCategories(): Promise<Category[]> {
    const response = await httpClient.get<Category[]>('/admin/categories');
    return response.data;
  }

  async createCategory(data: {
    name: string;
    description: string;
    displayOrder: number;
  }): Promise<Category> {
    const response = await httpClient.post<Category>('/admin/categories', data);
    return response.data;
  }

  async updateCategory(
    id: string,
    data: { name: string; description: string; displayOrder: number },
  ): Promise<Category> {
    const response = await httpClient.put<Category>(`/admin/categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: string): Promise<void> {
    await httpClient.delete(`/admin/categories/${id}`);
  }

  async getSubcategories(categoryId?: string): Promise<AdminSubcategory[]> {
    const params = categoryId ? { categoryId } : undefined;
    const response = await httpClient.get<AdminSubcategory[]>('/admin/subcategories', { params });
    return response.data;
  }

  async createSubcategory(data: {
    name: string;
    description: string;
    categoryId: string;
    price: number;
    displayOrder: number;
  }): Promise<void> {
    await httpClient.post('/admin/subcategories', data);
  }

  async updateSubcategory(
    id: string,
    data: {
      name: string;
      description: string;
      categoryId: string;
      price: number;
      displayOrder: number;
    },
  ): Promise<void> {
    await httpClient.put(`/admin/subcategories/${id}`, data);
  }

  async deleteSubcategory(id: string): Promise<void> {
    await httpClient.delete(`/admin/subcategories/${id}`);
  }

  async getChapters(subcategoryId?: string): Promise<AdminChapter[]> {
    const params = subcategoryId ? { subcategoryId } : undefined;
    const response = await httpClient.get<AdminChapter[]>('/admin/chapters', { params });
    return response.data;
  }

  async createChapter(data: {
    name: string;
    description: string;
    subcategoryId: string;
    displayOrder: number;
  }): Promise<void> {
    await httpClient.post('/admin/chapters', data);
  }

  async updateChapter(
    id: string,
    data: {
      name: string;
      description: string;
      subcategoryId: string;
      displayOrder: number;
    },
  ): Promise<void> {
    await httpClient.put(`/admin/chapters/${id}`, data);
  }

  async deleteChapter(id: string): Promise<void> {
    await httpClient.delete(`/admin/chapters/${id}`);
  }

  async getCourses(chapterId?: string): Promise<AdminCourse[]> {
    const params = chapterId ? { chapterId } : undefined;
    const response = await httpClient.get<AdminCourse[]>('/admin/courses', { params });
    return response.data;
  }

  async createCourse(
    data: {
      title: string;
      description: string;
      chapterId: string;
      duration: number;
    },
    files: NewCourseFiles,
    onProgress?: (progress: number) => void,
  ): Promise<void> {
    await httpClient.post(
      '/admin/courses',
      buildCourseFormData(data, files),
      uploadConfig(onProgress),
    );
  }

  async updateCourse(
    id: string,
    data: {
      title: string;
      description: string;
      chapterId: string;
      duration: number;
      displayOrder: number;
    },
    files: CourseUploadFiles,
    onProgress?: (progress: number) => void,
  ): Promise<void> {
    await httpClient.put(
      `/admin/courses/${id}`,
      buildCourseFormData(data, files),
      uploadConfig(onProgress),
    );
  }

  async deleteCourse(id: string): Promise<void> {
    await httpClient.delete(`/admin/courses/${id}`);
  }

  async removeCourseDocument(courseId: string, documentId: string): Promise<void> {
    await httpClient.delete(`/admin/courses/${courseId}/documents/${documentId}`);
  }
}
